/**
 * SAS REST APIのリクエストを扱うモジュール
 */
import plan from 'common/modules/domain/plan';
import { sasApiUrl } from '../urls';
import { createRequestId } from './http-request';

export enum LoginClientType {
  TYPE_OTHER = 'other',
  TYPE_BROWSER = 'browser'
}

const USER_AGENT = window.navigator.userAgent;

/**
 * クエリパラメータを作成する
 * # valueに値が無ければ作成しない
 *
 * @param params クエリパラメータのkeyとvalue
 */
function createQuery(
  params: { key: string; value: string | number | undefined }[]
) {
  const filtered = params.filter((p) => p.value);
  return filtered.length === 0
    ? ''
    : '?' + filtered.map((p) => `${p.key}=${p.value}`).join('&');
}

/**
 * BaseURLと結合してURLを返す
 */
const createUrl = (url: string) => {
  return url.match('^(http|https)://') ? url : sasApiUrl + url;
};

/**
 * ログインAPI
 * GET /v1/api/login
 * @param redirectUrl:ログイン後リダイレクトするURL(必須)
 * @param type:ログインするクライアントのタイプ
 */
export const getLoginRequest = (redirectUrl: string, type: string) => {
  const body = {
    redirectUrl: redirectUrl,
    clientType: type
  };
  const header = {
    'User-Agent': USER_AGENT,
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: createUrl('/v1/api/login'),
    config: {
      method: 'GET',
      headers: header,
      body: body
    }
  };
};

/**
 * アクセストークン取得API
 * GET /v1/api/token?code={code}&state={state}
 * @param code:CanonIDが発行する認可コード
 * @param state:ログインAPIのResponseHeaderに格納されている"Set-Cookie"の値("state="の部分は除く)
 */
export const getAccessTokenRequest = (code: string, state: string) => {
  const url = createUrl('/v1/api/token');
  const query = createQuery([
    { key: 'code', value: code },
    { key: 'state', value: state }
  ]);
  const header = {
    Cookie: `state=${state}`
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * 有効期限切れのアクセストークンをリフレッシュトークンで更新する
 * POST /v1/api/token
 * @param accessToken 更新するアクセストークン
 * @oaram refreshToken リフレッシュトークン
 */
export const postAccessTokenRequest = (
  accessToken: string,
  refreshToken: string
) => {
  const url = createUrl('/v1/api/token');
  const header = {
    Authorization: refreshToken,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8'
  };
  const bodyJson = {
    accessToken: accessToken
  };
  const body = JSON.stringify(bodyJson);
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/**
 * アクティベーション情報取得API
 * GET /v1/api/activation?{service_id}
 * @param token: SASが発行した独自トークン
 * @param serviceId: サービスID
 */
export const getActivationRequest = (token: string, serviceId: string) => {
  const url = createUrl('/v1/api/activation');
  const query = createQuery([{ key: 'serviceId', value: serviceId }]);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * ログアウトAPI
 * GET /v1/api/logout
 * @param token: SASが発行した独自トークン
 */
export const getLogoutRequest = (token: string) => {
  const header = {};
  const query = createQuery([{ key: 'token', value: token }]);
  const url = createUrl('/v1/api/logout');
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * プラン一覧取得API
 * GET /v1/api/plans?country={country}&serviceId={serviceId}&state={state}&nextKey={nextKey}
 */
export const getPlanListRequest = (
  country: string,
  language: string,
  serviceId: string,
  state: string,
  nextKey: string
) => {
  const url = createUrl('/v1/api/plans');
  const query = createQuery([
    { key: 'country', value: country },
    { key: 'language', value: language },
    { key: 'serviceId', value: serviceId },
    { key: 'state', value: state },
    { key: 'nextKey', value: nextKey }
  ]);
  const header = {
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * プラン取得API
 * GET /v1/api/plans/{planId}?country={country}&language={language}&state={state}&serviceId={serviceId}
 */
export const getPlanRequest = (
  planId: string,
  country: string,
  language: string,
  state: string,
  serviceId: string
) => {
  const url = createUrl(`/v1/api/plans/${planId}`);
  const query = createQuery([
    { key: 'country', value: country },
    { key: 'language', value: language },
    { key: 'state', value: state },
    { key: 'serviceId', value: serviceId }
  ]);
  const header = {
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * ユーザー情報取得API
 * GET /v1/api/users/userInfo
 */
export const getUserInfoRequest = (token: string) => {
  const url = createUrl(`/v1/api/users/userInfo`);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: url,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * サービス情報取得API
 * GET /v1/api/services/{serviceId}?language={language}
 */
export const getServiceRequest = (serviceId: string, language: string) => {
  const url = createUrl(`/v1/api/services/${serviceId}`);
  const query = createQuery([{ key: 'language', value: language }]);
  const header = {
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * ユーザーの購入履歴取得API
 * GET /v1/users/purchases?language={language}&state={state}&sort={sort}&from={履歴取得開始日時}&limit={limit}&nextKey={nextKey}
 */
export const getPurchaseHistoryRequest = (
  token: string,
  language: string,
  state: string,
  sort: string,
  from: string,
  nextKey: string
) => {
  const url = createUrl(`/v1/api/users/purchases`);
  const query = createQuery([
    { key: 'language', value: language },
    { key: 'state', value: state },
    { key: 'sort', value: sort },
    { key: 'from', value: from },
    { key: 'nextKey', value: nextKey }
  ]);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * 購入開始API
 * POST /v1/vendors/{vendorId}/purchase/session/
 */
export const postPurchaseSessionRequest = (
  token: string,
  vendorId: string,
  planId: string,
  country: string,
  language: string
) => {
  const url = createUrl(`/v1/api/vendors/${vendorId}/purchase/session/`);
  const bodyJson = {
    planId: planId,
    country: country,
    language: language
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/**
 * 解約API
 * DELETE /v1/api/vendors/{vendorId}/purchase/{purchaseId}
 */
export const deletePurchaseRequest = (
  token: string,
  vendorId: string,
  purchaseId: string
) => {
  const url = createUrl(`/v1/api/vendors/${vendorId}/purchase/${purchaseId}`);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: url,
    config: {
      method: 'DELETE',
      headers: header
    }
  };
};

/**
 * 個別解約API
 * POST /v1/api/vendors/{vendorId}/purchase/{purchaseId}/cancel
 */
export const postIndividualCancelRequest = (
  token: string,
  vendorId: string,
  purchaseId: string,
  indexes: string[]
) => {
  const url = createUrl(
    `/v1/api/vendors/${vendorId}/purchase/${purchaseId}/cancel`
  );
  const bodyJson = {
    indexes: indexes
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/**
 * 購入プランの利用実績取得API
 * GET /v1/api/users/purchases/{ユーザー注文番号(SAS発行)}/usage?limit={limit}&nextKey={nextKey}
 */
export const getLendingUserRequest = (
  token: string,
  purchaseId: string,
  nextKey: string
) => {
  const url = createUrl(`/v1/api/users/purchases/${purchaseId}/usage`);
  const query = createQuery([{ key: 'nextKey', value: nextKey }]);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * 貸与ユーザー情報登録API
 * POST /v1/api/users/purchases/{ユーザー注文番号(SAS発行)}/accounts
 */
export const postLendingUserRequest = (
  token: string,
  purchaseId: string,
  email: string
) => {
  const url = createUrl(`/v1/api/users/purchases/${purchaseId}/accounts`);
  const bodyJson = {
    email: email
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/** レンタル解除API
 * POST /v1/api/users/purchases/{ユーザー注文番号(SAS発行)}/accounts/cancel
 */
export const postLendingUserCancelRequest = (
  token: string,
  purchaseId: string,
  indexes: string[]
) => {
  const url = createUrl(
    `/v1/api/users/purchases/${purchaseId}/accounts/cancel`
  );
  const bodyJson = {
    indexes: indexes
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/** レンタル解除API(同期版)
 * POST /v1/api/users/purchases/{ユーザー注文番号(SAS発行)}/accounts/cancel/sync
 */
export const postLendingUserCancelSyncRequest = (
  token: string,
  purchaseId: string,
  index: string
) => {
  const url = createUrl(
    `/v1/api/users/purchases/${purchaseId}/accounts/cancel/sync`
  );
  const bodyJson = {
    index: index
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/** 第三者利用権返却API
 * DELETE /v1/api/users/purchases/{ユーザー注文番号(SAS発行)}/return
 */
export const deleteLendingReturnRequest = (
  token: string,
  purchaseId: string
) => {
  const url = createUrl(`/v1/api/users/purchases/${purchaseId}/return`);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: url,
    config: {
      method: 'DELETE',
      headers: header
    }
  };
};

/**
 * 端末情報一覧取得API
 * GET /v1/api/activation/terminals?purchaseId={ユーザー注文番号(SAS発行)}&limit={limit}&nextKey={nextKey}
 * @param token: SASが発行した独自トークン
 * @param purchaseId: ユーザー注文番号
 * @param nextKey: nextKey
 */
export const getTerminalsActivationRequest = (
  token: string,
  purchaseId: string,
  nextKey: string
) => {
  const url = createUrl('/v1/api/activation/terminals');
  const query = createQuery([
    { key: 'purchaseId', value: purchaseId },
    { key: 'nextKey', value: nextKey }
  ]);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * 端末削除API
 * POST /v1/api/activation/terminals/cancel
 * @param token: SASが発行した独自トークン
 * @param terminalIds: 端末IDのリスト
 * @param purchaseId: ユーザー注文番号
 */
export const postActivationTerminalsCancelRequest = (
  token: string,
  terminalIds: string[],
  purchaseId: string
) => {
  const url = createUrl('/v1/api/activation/terminals/cancel');
  const bodyJson = {
    terminalIds: terminalIds,
    purchaseId: purchaseId
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/**
 * 有償ファームウェアのアクティベート一覧取得API
 * GET /v1/api/activation/devices
 * @param token: SASが発行した独自トークン
 * @param purchaseId: ユーザー注文番号
 * @param nextKey: nextKey
 */
export const getDevicesActivationRequest = (
  token: string,
  purchaseId: string,
  nextKey: string
) => {
  const url = createUrl(`/v1/api/activation/devices`);
  const query = createQuery([
    { key: 'purchaseId', value: purchaseId },
    { key: 'nextKey', value: nextKey }
  ]);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId()
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'GET',
      headers: header
    }
  };
};

/**
 * 有償ファームウェアのアクティベート予約API
 * POST /v1/api/activation/devices/reserve
 * @param token: SASが発行した独自トークン
 * @param purchaseId: ユーザー注文番号
 * @param reqDatas: REQファイルデータのリスト[{reqVersion: REQファイルバージョン, firmwareVersion:ファームバージョン, modelName:モデル名, keyId: キーID(WW SASで作成したuuid), data:暗号化データ文字列(Base64エンコード済み)}]
 * @param isDryRun: 実行モード(DryRynモード:true、通常モード:false(省略時はfalse))
 * @param language: 言語コード(省略時はデフォルト言語の"en"がバックエンド側で指定される)
 */
export const postDevicesActivationReserveRequest = (
  token: string,
  purchaseId: string,
  reqDatas: [],
  isDryRun?: boolean,
  language?: string
) => {
  const url = createUrl('/v1/api/activation/devices/reserve');
  const bodyJson = {
    purchaseId: purchaseId,
    reqDatas: reqDatas,
    language: language,
    dryRun: isDryRun
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};

/**
 * 有償ファームウェアのアクティベート確定API
 * PUT /v1/api/activation/devices
 * @param token: SASが発行した独自トークン
 * @param purchaseId: ユーザー注文番号
 * @param nextKey: nextKey
 */
export const putDevicesActivationOutputRequest = (
  token: string,
  purchaseId: string,
  nextKey: string
) => {
  const url = createUrl('/v1/api/activation/devices');
  const query = createQuery([{ key: 'nextKey', value: nextKey }]);
  const bodyJson = {
    purchaseId: purchaseId
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: `${url}${query}`,
    config: {
      method: 'PUT',
      headers: header,
      body: body
    }
  };
};

/**
 * 有償ファームウェアのアクティベート予約削除API
 * POST /v1/api/activation/devices/reserve/cancel
 * @param token: SASが発行した独自トークン
 * @param purchaseId: ユーザー注文番号
 * @param modelId: 機種ID
 * @param serialNo: シリアル番号
 */
export const postDevicesActivationReserveCancelRequest = (
  token: string,
  purchaseId: string,
  modelId: string,
  serialNo: string
) => {
  const url = createUrl('/v1/api/activation/devices/reserve/cancel');
  const bodyJson = {
    purchaseId: purchaseId,
    modelId: modelId,
    serialNo: serialNo
  };
  const body = JSON.stringify(bodyJson);
  const header = {
    Authorization: token,
    'User-Agent': USER_AGENT,
    'sas-client-request-id': createRequestId(),
    'Content-Type': 'application/json;charset=utf-8',
    'Content-Length': JSON.stringify(body).length + ''
  };
  return {
    url: url,
    config: {
      method: 'POST',
      headers: header,
      body: body
    }
  };
};
